import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { Palmtree, Plus, Loader2, Calendar, Check, X, Clock, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, differenceInDays, addMonths, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';

// Calcul de la date de Pâques (algorithme de Meeus/Jones/Butcher)
const getEasterDate = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};

// Calcul du Jeûne genevois (jeudi après le premier dimanche de septembre)
const getJeuneGenevois = (year) => {
  const sept1 = new Date(year, 8, 1); // 1er septembre
  const dayOfWeek = sept1.getDay(); // 0 = dimanche
  // Trouver le premier dimanche de septembre
  const firstSunday = dayOfWeek === 0 ? sept1 : new Date(year, 8, 1 + (7 - dayOfWeek));
  // Le jeudi suivant = dimanche + 4 jours
  return new Date(year, 8, firstSunday.getDate() + 4);
};

// Obtenir tous les jours fériés genevois pour une année donnée
const getGenevaHolidays = (year) => {
  const easter = getEasterDate(year);
  
  return [
    { date: new Date(year, 0, 1), name: "Nouvel An" },
    { date: new Date(easter.getTime() - 2 * 24 * 60 * 60 * 1000), name: "Vendredi Saint" },
    { date: new Date(easter.getTime() + 1 * 24 * 60 * 60 * 1000), name: "Lundi de Pâques" },
    { date: new Date(easter.getTime() + 39 * 24 * 60 * 60 * 1000), name: "Jeudi de l'Ascension" },
    { date: new Date(easter.getTime() + 50 * 24 * 60 * 60 * 1000), name: "Lundi de Pentecôte" },
    { date: new Date(year, 7, 1), name: "Fête nationale" },
    { date: getJeuneGenevois(year), name: "Jeûne genevois" },
    { date: new Date(year, 11, 25), name: "Noël" },
    { date: new Date(year, 11, 31), name: "Saint-Sylvestre" }
  ];
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const leaveTypes = [
  { value: 'vacation', label: 'Vacances', color: 'bg-blue-100 text-blue-700' },
  { value: 'sick', label: 'Maladie', color: 'bg-red-100 text-red-700' },
  { value: 'personal', label: 'Personnel', color: 'bg-purple-100 text-purple-700' },
  { value: 'other', label: 'Autre', color: 'bg-slate-100 text-slate-700' }
];

const statusConfig = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approuvé', color: 'bg-emerald-100 text-emerald-700', icon: Check },
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700', icon: X }
};

export default function LeavesPage() {
  const { user, token, isAdmin } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ leave_type: 'vacation', start_date: '', end_date: '', reason: '' });
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);

  // Calculer les jours fériés pour l'année en cours et suivante
  const holidays = useMemo(() => {
    const currentYear = calendarMonth.getFullYear();
    return [
      ...getGenevaHolidays(currentYear - 1),
      ...getGenevaHolidays(currentYear),
      ...getGenevaHolidays(currentYear + 1)
    ];
  }, [calendarMonth]);

  // Dates des jours fériés pour le calendrier
  const holidayDates = useMemo(() => holidays.map(h => h.date), [holidays]);

  // Trouver le nom du jour férié pour une date
  const getHolidayName = (date) => {
    const holiday = holidays.find(h => 
      h.date.getDate() === date.getDate() && 
      h.date.getMonth() === date.getMonth() && 
      h.date.getFullYear() === date.getFullYear()
    );
    return holiday?.name;
  };

  useEffect(() => {
    fetchLeaves();
  }, []);

  const fetchLeaves = async () => {
    try {
      const res = await fetch(`${API_URL}/api/leaves`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLeaves(data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date) {
      toast.error('Veuillez remplir les dates');
      return;
    }
    if (new Date(form.end_date) < new Date(form.start_date)) {
      toast.error('La date de fin doit être après la date de début');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/leaves`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success('Demande de congé envoyée');
        setDialogOpen(false);
        setForm({ leave_type: 'vacation', start_date: '', end_date: '', reason: '' });
        fetchLeaves();
      } else {
        toast.error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (leave, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/leaves/${leave.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        toast.success(`Demande ${newStatus === 'approved' ? 'approuvée' : 'refusée'}`);
        fetchLeaves();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (leaveId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette demande de congé ?')) return;
    try {
      const res = await fetch(`${API_URL}/api/leaves/${leaveId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Demande supprimée');
        fetchLeaves();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getLeaveTypeConfig = (type) => leaveTypes.find(t => t.value === type) || leaveTypes[3];
  const getDuration = (start, end) => differenceInDays(new Date(end), new Date(start)) + 1;

  const stats = {
    pending: leaves.filter(l => l.status === 'pending').length,
    approved: leaves.filter(l => l.status === 'approved').length,
    rejected: leaves.filter(l => l.status === 'rejected').length
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leaves-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Congés</h1>
          <p className="text-slate-500 mt-1">Gérez vos demandes de congés</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowCalendar(!showCalendar)} 
            variant="outline"
            className={`border-slate-300 ${showCalendar ? 'bg-slate-100' : ''}`}
          >
            <Calendar className="w-4 h-4 mr-2" />Calendrier
          </Button>
          <Button onClick={() => setDialogOpen(true)} className="bg-slate-900 hover:bg-slate-800">
            <Plus className="w-4 h-4 mr-2" />Nouvelle demande
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <Clock className="w-6 h-6 text-amber-500 mb-2" />
            <p className="text-sm text-slate-500">En attente</p>
            <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <Check className="w-6 h-6 text-emerald-500 mb-2" />
            <p className="text-sm text-slate-500">Approuvés</p>
            <p className="text-2xl font-bold text-emerald-600">{stats.approved}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <X className="w-6 h-6 text-red-500 mb-2" />
            <p className="text-sm text-slate-500">Refusés</p>
            <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
          </CardContent>
        </Card>
      </div>

      {/* Calendrier des jours fériés - Toggle */}
      {showCalendar && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-red-500" />
              Jours fériés - Canton de Genève
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Calendrier */}
              <div className="flex-1 flex justify-center">
                <CalendarComponent
                  mode="single"
                  month={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  locale={fr}
                  modifiers={{
                    holiday: holidayDates
                  }}
                  modifiersStyles={{
                    holiday: { 
                      backgroundColor: '#fee2e2', 
                      color: '#dc2626',
                      fontWeight: 'bold',
                      borderRadius: '50%'
                    }
                  }}
                  onDayClick={(day) => {
                    const holidayName = getHolidayName(day);
                    if (holidayName) {
                      setSelectedHoliday({ date: day, name: holidayName });
                    } else {
                      setSelectedHoliday(null);
                    }
                  }}
                  className="rounded-md border"
                />
              </div>
              
              {/* Liste des jours fériés de l'année */}
              <div className="flex-1">
                <h3 className="font-semibold text-slate-700 mb-3">
                  Jours fériés {calendarMonth.getFullYear()}
                </h3>
                <div className="space-y-2">
                  {getGenevaHolidays(calendarMonth.getFullYear()).map((holiday, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center justify-between p-2 rounded-lg transition-colors ${
                        selectedHoliday?.name === holiday.name && 
                        selectedHoliday?.date.getFullYear() === holiday.date.getFullYear()
                          ? 'bg-red-100 border border-red-300' 
                          : 'bg-slate-50 hover:bg-slate-100'
                      }`}
                    >
                      <span className="text-sm font-medium text-slate-700">{holiday.name}</span>
                      <Badge className="bg-red-100 text-red-700">
                        {format(holiday.date, 'dd MMM', { locale: fr })}
                      </Badge>
                    </div>
                  ))}
                </div>
                {selectedHoliday && (
                  <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm text-red-700">
                      <span className="font-semibold">{selectedHoliday.name}</span>
                      <br />
                      {format(selectedHoliday.date, 'EEEE dd MMMM yyyy', { locale: fr })}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leaves list */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palmtree className="w-5 h-5 text-emerald-500" />
            {isAdmin ? 'Toutes les demandes' : 'Mes demandes'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {leaves.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Palmtree className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Aucune demande de congé</p>
            </div>
          ) : (
            <div className="space-y-4">
              {leaves.map((leave) => {
                const typeConfig = getLeaveTypeConfig(leave.leave_type);
                const status = statusConfig[leave.status];
                const StatusIcon = status.icon;
                return (
                  <div key={leave.id} className="p-4 rounded-lg border hover:bg-slate-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {isAdmin && <span className="font-medium">{leave.user_name}</span>}
                          <Badge className={typeConfig.color}>{typeConfig.label}</Badge>
                          <Badge className={status.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {status.label}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-600">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {format(new Date(leave.start_date), 'dd MMM', { locale: fr })} - {format(new Date(leave.end_date), 'dd MMM yyyy', { locale: fr })}
                          </span>
                          <span className="font-medium">{getDuration(leave.start_date, leave.end_date)} jour(s)</span>
                        </div>
                        {leave.reason && <p className="text-sm text-slate-500 mt-2">{leave.reason}</p>}
                      </div>
                      <div className="flex gap-2">
                        {isAdmin && leave.status === 'pending' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleStatusChange(leave, 'approved')} className="text-emerald-600 border-emerald-200 hover:bg-emerald-50">
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleStatusChange(leave, 'rejected')} className="text-red-600 border-red-200 hover:bg-red-50">
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {/* Delete button - admin can delete any, user can delete own pending */}
                        {(isAdmin || (leave.user_id === user?.id && leave.status === 'pending')) && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => handleDelete(leave.id)} 
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvelle demande de congé</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label>Type de congé</Label>
              <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {leaveTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de début *</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Date de fin *</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>
            {form.start_date && form.end_date && new Date(form.end_date) >= new Date(form.start_date) && (
              <p className="text-sm text-slate-500">Durée: {getDuration(form.start_date, form.end_date)} jour(s)</p>
            )}
            <div className="space-y-2">
              <Label>Motif</Label>
              <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={3} placeholder="Optionnel" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
